import React, { Suspense, useEffect, useRef } from 'react';
import { useTheme } from '@/providers/ThemeProvider';
import { Loader2 } from 'lucide-react';
import type { editor } from 'monaco-editor';

// Lazy load Monaco Editor
const MonacoEditor = React.lazy(() => import('@monaco-editor/react'));

interface MonacoWrapperProps {
  value: string;
  onChange: (value: string | undefined) => void;
  onValidate?: (markers: editor.IMarkerData[]) => void;
  readOnly?: boolean;
  className?: string;
}

function MonacoLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full bg-background">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground text-sm">
          Loading code editor...
        </span>
      </div>
    </div>
  );
}

export function MonacoWrapper({
  value,
  onChange,
  onValidate,
  readOnly = false,
  className = '',
}: MonacoWrapperProps) {
  const { theme } = useTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  // Monaco editor options
  const monacoOptions: editor.IStandaloneEditorConstructionOptions = {
    readOnly,
    automaticLayout: true,
    formatOnPaste: true,
    formatOnType: true,
    minimap: { enabled: true, scale: 0.75 },
    scrollBeyondLastLine: false,
    fontSize: 13,
    tabSize: 2,
    wordWrap: 'on',
    lineNumbers: 'on',
    folding: true,
    foldingStrategy: 'indentation',
    bracketPairColorization: { enabled: true },
    scrollbar: {
      vertical: 'auto',
      horizontal: 'auto',
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
    },
    padding: { top: 16, bottom: 16 },
  };

  const handleEditorMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    // Format document on mount
    setTimeout(() => {
      editor.getAction('editor.action.formatDocument')?.run();
    }, 100);
  };

  const handleEditorValidation = (markers: editor.IMarkerData[]) => {
    if (onValidate) {
      onValidate(markers);
    }
  };

  return (
    <div className={`h-full w-full ${className}`}>
      <Suspense fallback={<MonacoLoadingFallback />}>
        <MonacoEditor
          height="100%"
          language="json"
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          value={value}
          onChange={onChange}
          onMount={handleEditorMount}
          onValidate={handleEditorValidation}
          options={monacoOptions}
        />
      </Suspense>
    </div>
  );
}
